import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { InfoBusinessCard } from '@/blocks/components/InfoBusinessCard';
import { LinkSignin } from '@/blocks/components/PlatformLink';
import { Separator } from '@/blocks/components/Separator';
import { FormatPercent } from '@/blocks/formatters';
import { FormatAmount } from '@/blocks/formatters/format-price';
import { useFaqController } from '@/providers/FAQProvider';
import links from '@/styles/link.module.scss';
import typography from '@/styles/typography.module.scss';

import {
  calculateTakerFee,
  constantMakerFee,
  getMonthlyVolumeBounds,
} from '@/app/[lang]/crypto-futures/data';

import styles from './styles.module.scss';

export const CFAdvantagesTradingTable = () => {
  const tr = useTranslations();
  const monthlyVolumeBounds = getMonthlyVolumeBounds();
  const { faqController } = useFaqController();

  return (
    <div className={styles.tradingGrid}>
      <div className={styles.tradingIndicators}>
        <div className={styles.indicatorsTitle}>
          {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.title', {
            percent: () => (
              <AccentFragment>
                <FormatPercent value={constantMakerFee} />
              </AccentFragment>
            ),
          })}
        </div>
        <div className={styles.verticalCard}>
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.volume', {
              amount: () => (
                <span className={styles.weightAccent}>
                  &#60;
                  <FormatAmount value={monthlyVolumeBounds.bottom} />
                </span>
              ),
            })}
          </span>
          <Separator />
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.takerFee', {
              percent: () => (
                <span className={styles.weightAccent}>
                  <FormatPercent
                    value={calculateTakerFee(monthlyVolumeBounds.bottom - 1)}
                    fraction={{ max: 3 }}
                  />
                </span>
              ),
            })}
          </span>
        </div>
        <div className={styles.verticalCard}>
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.volume', {
              amount: () => (
                <span className={styles.weightAccent}>
                  &#8805;
                  <FormatAmount value={monthlyVolumeBounds.bottom} />
                </span>
              ),
            })}
          </span>
          <Separator />
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.takerFee', {
              percent: () => (
                <span className={styles.weightAccent}>
                  <FormatPercent
                    value={calculateTakerFee(monthlyVolumeBounds.bottom + 1)}
                    fraction={{ max: 3 }}
                  />
                </span>
              ),
            })}
          </span>
        </div>
        <div className={styles.verticalCard}>
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.volume', {
              amount: () => (
                <span className={styles.weightAccent}>
                  &#8805;
                  <FormatAmount value={monthlyVolumeBounds.top} />
                </span>
              ),
            })}
          </span>
          <Separator />
          <span className={typography.subheadingSM}>
            {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.takerFee', {
              percent: () => (
                <span className={styles.weightAccent}>
                  <FormatPercent
                    value={calculateTakerFee(monthlyVolumeBounds.top + 1)}
                    fraction={{ max: 2 }}
                  />
                </span>
              ),
            })}
          </span>
        </div>
        <span className={styles.disclaimer}>
          {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.indicators.disclaimer', {
            a: (content) => (
              <button
                onClick={() => faqController.scrollToFaq(2)}
                className={classNames(links.underline, links.textLink)}
              >
                {content}
              </button>
            ),
          })}
        </span>
      </div>
      <InfoBusinessCard
        withSeparator
        title={tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.0.title')}
        subheading={tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.0.description')}
        className={styles.infoCard}
        subheadingClassName={styles.infoCardSubheading}
      >
        <div className={typography.subheadingSM}>
          {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.0.action', {
            a: (content) => (
              <LinkSignin
                params={{ redirect: '/my/fees' }}
                className={classNames(links.underline, links.textLink)}
              >
                {content}
              </LinkSignin>
            ),
          })}
        </div>
      </InfoBusinessCard>
      <InfoBusinessCard
        withSeparator
        title={tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.1.title')}
        subheading={tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.1.description')}
        className={styles.infoCard}
        subheadingClassName={styles.infoCardSubheading}
      >
        <div className={typography.subheadingSM}>
          {tr.rich('pages.cryptoFutures.fees.sections.advantages.trading.info.cards.1.action', {
            a: (content) => (
              <LinkSignin
                params={{ redirect: '/my/fees' }}
                className={classNames(links.underline, links.textLink)}
              >
                {content}
              </LinkSignin>
            ),
          })}
        </div>
      </InfoBusinessCard>
    </div>
  );
};
